//import React, { useState, useEffect } from 'react';
import React, { useState, useEffect, useMemo } from "react";
import * as XLSX from 'xlsx';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import {
    Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TablePagination, TableFooter
} from '@mui/material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import ReportShell, { RC, dateInp, refreshBtn, excelBtn } from './ReportShell';


function SaleAnalysisReport() {
    const [dataBill, setDataBill] = useState([]);
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    const tableRef = React.createRef();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [financialDate, setFinancialDate] = useState('');
    const [selectedOption, setSelectedOption] = useState('itemWise');
    const [customerId, setCustomer] = useState('');
    const { isAuthenticated, companyId, permissions, token, username, logout } = useAuth();
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        setFinancialDate(getFormattedStartFinancialDate());
    }, [isAuthenticated, companyId, token, logout]);

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const today = new Date();
        let year = today.getFullYear();

        if (today.getMonth() + 1 > 3) {
            // If the current month is greater than 3, use the current year
            return `${year}-04-01`;
        } else {
            // If the current month is 3 or less, use the previous year
            return `${year - 1}-04-01`;
        }
    };

    const getFormattedStartFinancialDate = () => {
        const monthStartFinancialDate = new Date();
        monthStartFinancialDate.setDate(1);
        return monthStartFinancialDate.toISOString().split('T')[0];
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFromDate(selectedDate);
    };

    const handleFinancialDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setFinancialDate(selectedDate);
    };

    // Similar function for toDate
    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        // Add validation if needed
        setToDate(selectedDate);
    };

    const hasReadReportPermission = permissions.includes('Read Reports');

    const handleChange = (event) => {
        setSelectedOption(event.target.value);
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const filteredDataBill = dataBill.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const [isLoading, setIsLoading] = useState(false);

    // const filteredDataItem = useMemo(() => {
    //     return dataItem;
    // }, [dataItem]);

    const fetchTableData = async () => {
        setDataItem([]);
        setIsLoading(true);
        try {
            const response = await fetchData('ReportApi', `/SalesAnalysis/${fromDate}/${toDate}/${companyId}`, token);
            if (response.responseCode === "1111") {
                setDataItem(response.response_data);
            } else {
                console.warn("Unexpected response code:", response.responseCode);
            }
        } catch (error) {
            console.error("Error fetching Trial Balance Details:", error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchTableData();
    }, []);



    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Sale Analysis Details');

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

        // Add header caption (merge across all columns)
        worksheet.mergeCells('A1:E1');
        const captionCell = worksheet.getCell('A1');
        captionCell.value = `Sale Analysis Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)} | Generated On: ${formattedDateTime} By : ${username}`;
        captionCell.alignment = { horizontal: 'center' };
        captionCell.font = { bold: true, size: 12 };
        captionCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } }; // Light gray

        // Add column headers
        const headerRow = worksheet.addRow(['Item Name', 'Monthly Avg', 'Quarterly Avg', 'Halfyear Avg', 'Yearly Avg']);
        headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }; // White text
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } }; // Blue background
            cell.alignment = { horizontal: 'center' };
            cell.border = {
                top: { style: 'thin' },
                bottom: { style: 'thin' },
                left: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        // Add data rows
        filteredDataItem.forEach((rowData) => {
            const row = worksheet.addRow([
                rowData.itemName,
                parseFloat(rowData.monthlyavg) || 0,
                parseFloat(rowData.quarterlyavg) || 0,
                parseFloat(rowData.halfyearavg) || 0,
                parseFloat(rowData.yearlyavg) || 0
            ]);
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                };
            });
        });

        // Set column widths
        worksheet.columns.forEach((col) => (col.width = 20));

        // Save file
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Sale_Analysis.xlsx');
    };


    const [page, setPage] = useState(0); // Current page
    const [rowsPerPage, setRowsPerPage] = useState(15); // Rows per page

    // Define initial column order
    const [columnOrder, setColumnOrder] = useState([
        'Item Name',
        'Monthly Avg',
        'Quarterly Avg',
        'Halfyear Avg',
        'Yearly Avg',
    ]);

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0); // Reset to the first page
    };

    // Function to handle drag start
    const handleDragStart = (e, index) => {
        e.dataTransfer.setData('columnIndex', index);
    };

    // Function to handle drag over
    const handleDragOver = (e) => {
        e.preventDefault(); // Allow drop
    };

    // Function to handle drop
    const handleDrop = (e, index) => {
        const draggedIndex = e.dataTransfer.getData('columnIndex');
        if (draggedIndex !== index) {
            const newOrder = [...columnOrder];
            // Swap columns
            [newOrder[draggedIndex], newOrder[index]] = [newOrder[index], newOrder[draggedIndex]];
            setColumnOrder(newOrder);
        }
    };

    // Process and paginate the data
    const processedData = filteredDataItem.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );



    // KPI values
    const monthlyTotal = processedData.reduce((a, b) => a + (b.monthlyavg || 0), 0);
    const quarterlyTotal = processedData.reduce((a, b) => a + (b.quarterlyavg || 0), 0);
    const halfyearTotal = processedData.reduce((a, b) => a + (b.halfyearavg || 0), 0);
    const yearlyTotal = processedData.reduce((a, b) => a + (b.yearlyavg || 0), 0);

    return (
        <ReportShell
            title="SALE ANALYSIS"
            recordCount={filteredDataItem.length}
            loading={isLoading}
            filters={<>
                <span style={{ fontSize: '10px', color: RC.muted, marginRight: '4px' }}>From</span>
                <input type="date" value={fromDate} onChange={handleFromDateChange} style={{ ...dateInp, width: '130px' }} />
            </>}
            actions={<>
                <button onClick={fetchTableData} style={refreshBtn}>Refresh</button>
                <button onClick={handleExportExcel} style={excelBtn}>Excel</button>
            </>}
        >
                <TableContainer>
                    <Table stickyHeader size="small">
                        {/* Header */}
                        <TableHead>
                            <TableRow>
                                {columnOrder.map((col, index) => (
                                    <TableCell
                                        key={index}
                                        style={{
                                            background: "#e9ecef",
                                            fontWeight: "600",
                                            fontSize: "11px",
                                            padding: "2px",
                                            borderBottom: "2px solid #dee2e6",
                                            textAlign: col === "Item Name" ? "left" : "right",
                                            textTransform: 'uppercase'
                                        }}
                                    >
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* Body */}
                        <TableBody>
                            {processedData
                                .filter((rowData) => rowData.CustomerName !== "Total")
                                .map((rowData, index) => {
                                    const rowValues = {
                                        "Item Name": rowData.itemName || "",
                                        "Monthly Avg": rowData.monthlyavg
                                            ? parseFloat(rowData.monthlyavg).toFixed(2)
                                            : "0.00",
                                        "Quarterly Avg": rowData.quarterlyavg
                                            ? parseFloat(rowData.quarterlyavg).toFixed(2)
                                            : "0.00",
                                        "Halfyear Avg": rowData.halfyearavg
                                            ? parseFloat(rowData.halfyearavg).toFixed(2)
                                            : "0.00",
                                        "Yearly Avg": rowData.yearlyavg
                                            ? parseFloat(rowData.yearlyavg).toFixed(2)
                                            : "0.00",
                                    };

                                    return (
                                        <TableRow
                                            key={rowData.Id || index}
                                            sx={{
                                                backgroundColor:
                                                    index % 2 === 0 ? "#ffffff" : "#f9f9f9",
                                                "&:hover": {
                                                    backgroundColor: "#f1f3f5",
                                                    cursor: 'pointer'
                                                },
                                            }}
                                        >
                                            {columnOrder.map((col, idx) => (
                                                <TableCell
                                                    key={idx}
                                                    style={{
                                                        padding: "6px",
                                                        fontSize: "12.5px",
                                                        borderBottom: "1px solid #e9ecef",
                                                        textAlign: col === "Item Name" ? "left" : "right",
                                                    }}
                                                >
                                                    {rowValues[col]}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                        </TableBody>

                        {/* Footer */}
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    rowsPerPageOptions={[15, 25, 50, 100]}
                                    count={filteredDataItem.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handlePageChange}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
        </ReportShell>
    );
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}


export default SaleAnalysisReport;